/* @jsxImportSource react */

import type { FragmentType } from '@packages/gql/generated/shopping';
import type { EfficiencyFlagsAndDataSheetsDownloadCardFragmentFragmentDoc } from '@packages/gql/generated/shopping/EfficiencyFlagsAndDataSheetsDownloadCardFragmentFragmentDoc';
import { Card, CardContent } from '@packages/shared/src/ui/Card/Card';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import { unmask } from '@packages/gql/src/betterMasking';
import type { ProductDataSheetsDownloadsListFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductDataSheetsDownloadsListFragmentFragmentDoc';
import type { EfficiencyFlagListFragmentFragmentDoc } from '@packages/gql/generated/shopping/EfficiencyFlagListFragmentFragmentDoc';
import type { PropsWithChildren } from 'react';
import { ProductDataSheetsDownloadsList } from '../ProductDataSheetsDownloadsList/ProductDataSheetsDownloadsList';
import {
  EfficiencyFlagList,
  type EfficiencyFlagListProps,
} from '../EfficiencyFlagList/EfficiencyFlagList';
import { DownloadItem } from '../DownloadItem/DownloadItem.client';
import { PowerEfficiencyFlag } from '../EfficiencyFlagList/PowerEfficiencyFlag/PowerEfficiencyFlag.client';
import { TireEfficiencyFlag } from '../EfficiencyFlagList/TireEfficiencyFlag/TireEfficiencyFlag.client';

const CardWrapper = ({ children }: PropsWithChildren) => (
  <Card className="border-color-grey rounded-[4px] border shadow-none">{children}</Card>
);

/* GraphQL */ `
  fragment EfficiencyFlagsAndDataSheetsDownloadCardFragment on Product {
    ...EfficiencyFlagListFragment
    ...ProductDataSheetsDownloadsListFragment
  }
`;

export type EfficiencyFlagsAndDataSheetsDownloadCardProps = {
  /** List with efficiencyFlag properties to simply render component. */
  data: FragmentType<typeof EfficiencyFlagsAndDataSheetsDownloadCardFragmentFragmentDoc>;
} & Pick<EfficiencyFlagListProps, 'horizontal' | 'size'>;

/**
 * EfficiencyFlagsAndDataSheetsDownloadCard is a component which displays a list of the `EfficiencyFlags` and product data sheet downloads.
 * It has a special layout variant used in the detail view.
 * */
export const EfficiencyFlagsAndDataSheetsDownloadCard = ({
  data,
  horizontal,
  size,
}: EfficiencyFlagsAndDataSheetsDownloadCardProps) => {
  const unmaskedData =
    unmask<typeof EfficiencyFlagsAndDataSheetsDownloadCardFragmentFragmentDoc>(data);
  const { productDataSheetsDownloads } =
    unmask<typeof ProductDataSheetsDownloadsListFragmentFragmentDoc>(unmaskedData);
  const { powerEfficiencyFlags, tireEfficiency } =
    unmask<typeof EfficiencyFlagListFragmentFragmentDoc>(unmaskedData);

  // if there is only one flag (Energy or Tire) and one download this data should be side by side instead of top down
  if (
    productDataSheetsDownloads.length === 1 &&
    (powerEfficiencyFlags?.length || 0) + (tireEfficiency?.flags.length || 0) === 1
  ) {
    return (
      <CardWrapper>
        <CardContent className="flex flex-row items-center gap-2 p-1.5">
          {powerEfficiencyFlags?.length ? (
            <PowerEfficiencyFlag data={powerEfficiencyFlags[0]} showLabel={false} size={size} />
          ) : (
            // data must be availbale because of the condition
            <TireEfficiencyFlag data={tireEfficiency!.flags[0]} link={tireEfficiency!.link} />
          )}
          <DownloadItem
            renderIcon={false}
            type={productDataSheetsDownloads[0].type}
            link={productDataSheetsDownloads[0].link}
          />
        </CardContent>
      </CardWrapper>
    );
  }

  return (
    <CardWrapper>
      <CardContent className="p-1.5">
        <EfficiencyFlagList data={unmaskedData} horizontal={horizontal} size={size} />
      </CardContent>
      {productDataSheetsDownloads.length > 0 && (
        <>
          <Divider flexItem />
          <CardContent className="px-1.5 py-1">
            <ProductDataSheetsDownloadsList data={unmaskedData} />
          </CardContent>
        </>
      )}
    </CardWrapper>
  );
};
